import React, { useMemo } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
} from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { formatTimeOnly } from "../../utils/formatDateTime";
import { capitalizeFirstLetter } from "../../utils/capitalize";

const HEADERS = [
  { label: "Program Name", width: "24%" },
  { label: "Priority", width: "16%" },
  { label: "Recent Activity", width: "24%" },
  { label: "Assigned RN", width: "16%" },
  { label: "Upcoming Schedule", width: "20%" },
];

const checkIsBiosimilar = (prog) => {
  if (!prog) return false;
  if (typeof prog === "string")
    return prog.toUpperCase().includes("BIOSIMILAR");
  const name = String(
    prog.name ||
      prog.programName ||
      prog.programDisplayName ||
      prog.displayName ||
      prog.groupName ||
      prog.category ||
      "",
  ).toUpperCase();
  const id = String(prog.id || prog.programId || "").toUpperCase();
  return name.includes("BIOSIMILAR") || id.includes("BIOSIMILAR");
};

const fixDateString = (s) => {
  if (typeof s !== "string") return "";
  return s.replace(/(\.\d{3})\d+$/, "$1");
};

const formatDate = (dateValue) => {
  if (dateValue == null || dateValue === "" || dateValue === "N/A")
    return "N/A";
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const fixed = fixDateString(String(dateValue));
    const utcString = fixed.endsWith("Z") ? fixed : fixed + "Z";
    const d = new Date(utcString);
    if (Number.isNaN(d.getTime())) return dateValue;
    return d.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      timeZone: tz,
    });
  } catch (e) {
    console.error(e);
    return dateValue;
  }
};

const CELL_BORDER = {
  borderColor: "#E8E8E9",
  borderWidth: "0px 0.5px 0px 0.5px",
  borderStyle: "solid",
};

const PRIORITY_PILLS = [
  {
    key: "1",
    text: "PRIORITY 1",
    bgColor: "#FFF5F5",
    textColor: "#D32F2F",
    borderColor: "#FFCDD2",
  },
  {
    key: "2",
    text: "PRIORITY 2",
    bgColor: "#FFF9E6",
    textColor: "#B45309",
    borderColor: "#FFE082",
  },
  {
    key: "3",
    text: "PRIORITY 3",
    bgColor: "#EEF2FF",
    textColor: "#4F46E5",
    borderColor: "#C5CAE9",
  },
];

const getPriorityPill = (pLevel) => {
  const found = PRIORITY_PILLS.find((pill) => pLevel.includes(pill.key));
  if (!found) return null;
  const { key, ...pill } = found;
  return pill;
};

const getCensusIndicator = (prog, rawPriority, memberData) => {
  const match =
    rawPriority.match(/((Cd+))/i) ||
    rawPriority.match(/-s*(Cd+)/i) ||
    rawPriority.match(/(Cd+)/i);
  if (match) return match[1].toUpperCase();
  return (
    prog.censusIndicator ||
    prog.censusStatus ||
    memberData?.censusStatus ||
    memberData?.censusIndicator ||
    ""
  );
};

const getOutcomeColor = (prog, statusText) => {
  const rawColor =
    prog.outcomeColour ||
    prog.outcomeColor ||
    prog.outcome_colour ||
    prog.outcome_color ||
    prog.outcomeColorCode;
  if (rawColor) {
    const trimmed = String(rawColor).trim();
    return /^[0-9A-Fa-f]{3,8}$/.test(trimmed) ? `#${trimmed}` : trimmed;
  }
  return statusText.toUpperCase() === "ENROLLED" ? "#45B184" : "#2F2A5C";
};

const getFormattedNextAssessment = (prog, getNextScheduledAssessment) => {
  let formattedNextAssessment = null;
  if (prog.nextAssessmentType && prog.nextScheduledStartTime) {
    try {
      const dateObj = new Date(prog.nextScheduledStartTime);
      const formattedDate = `${String(dateObj.getMonth() + 1).padStart(2, "0")}/${String(dateObj.getDate()).padStart(2, "0")}/${dateObj.getFullYear()}`;
      const cleanType = prog.nextAssessmentType
        .replaceAll("_", " ")
        .toLowerCase();
      formattedNextAssessment = (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CalendarTodayIcon sx={{ fontSize: 16, color: "#403C6F" }} />
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 500,
              color: "#1B2F3F",
              textTransform: "capitalize",
            }}
          >
            {`${cleanType} - ${formattedDate}`}
          </Typography>
        </Box>
      );
    } catch (e) {
      console.error("Failed to parse nextScheduledStartTime", e);
    }
  }

  if (!formattedNextAssessment) {
    const visit = getNextScheduledAssessment(prog.name);
    formattedNextAssessment = visit ? (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <CalendarTodayIcon sx={{ fontSize: 16, color: "#403C6F" }} />
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: 500,
            color: "#1B2F3F",
          }}
        >
          {`${visit.assessmentType} - ${String(visit.month + 1).padStart(2, "0")}/${String(visit.day).padStart(2, "0")}/${visit.year}`}
        </Typography>
      </Box>
    ) : (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <CalendarTodayIcon sx={{ fontSize: 16, color: "#66638C" }} />
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: 500,
            color: "#66638C",
          }}
        >
          None Scheduled
        </Typography>
      </Box>
    );
  }
  return formattedNextAssessment;
};

const ClinicalProgramsTable = ({
  memberCgProgramsList = [],
  memberData,
  getNextScheduledAssessment,
  getCategoryIcon,
  canWrite = true,
  onAddProgramOutcome,
}) => {
  const hasBiosimilarProgram = useMemo(() => {
    return (memberCgProgramsList || []).some((prog) => checkIsBiosimilar(prog));
  }, [memberCgProgramsList]);

  const tableHeaders = useMemo(() => {
    if (hasBiosimilarProgram) {
      return [
        { label: "Program Name", width: "20%" },
        { label: "Priority", width: "14%" },
        { label: "Recent Activity", width: "20%" },
        { label: "Assigned RN", width: "15%" },
        { label: "Upcoming Schedule", width: "16%" },
        { label: "Actions", width: "15%" },
      ];
    }
    return HEADERS;
  }, [hasBiosimilarProgram]);
  return (
    <Box
      sx={{
        mt: 1,
        flex: 1,
        minHeight: 0,
        height: "100%",
        pb: 3,
        borderRadius: 2,
        backgroundColor: "#FFFFFF",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <TableContainer
        sx={{
          flex: 1,
          minHeight: 0,
          borderRadius: "8px",
          border: "1px solid #E8E8E9",
          overflowY: "auto",
          overflowX: "auto",
          "&::-webkit-scrollbar": {
            width: "6px",
            height: "6px",
          },
          "&::-webkit-scrollbar-track": {
            background: "#F5F5F5",
            borderRadius: "4px",
            margin: "4px 0",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#A7A5BD",
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "#8E8CA8",
          },
          scrollbarWidth: "thin",
          scrollbarColor: "#A7A5BD #F5F5F5",
        }}
      >
        <Table stickyHeader size="small" sx={{ minWidth: 1000 }}>
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: "#CFCEDA",
                position: "sticky",
                top: 0,
                zIndex: 5000,
                boxShadow: "0 2px 0 rgba(0,0,0,0.05)",
              }}
            >
              {tableHeaders.map((header) => (
                <TableCell
                  key={header.label}
                  sx={{
                    backgroundColor: "#CFCEDA",
                    color: "#403C6F",
                    fontWeight: 700,
                    fontSize: "14px",
                    py: 1.5,
                    borderBottom: "none",
                    whiteSpace: "nowrap",
                    width: header.width,
                    position: "sticky",
                    top: 0,
                    zIndex: 5000,
                    borderColor: "#f5f5f8ff",
                    borderWidth: "0px 1px 0px 1px",
                    borderStyle: "solid",
                  }}
                >
                  {header.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {memberCgProgramsList.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={tableHeaders.length}
                  align="center"
                  sx={{
                    py: 10,
                    color: "#66638C",
                    fontSize: "14px",
                    borderBottom: "none",
                  }}
                >
                  This member is not currently eligible for any Clinical
                  Programs (CG+)
                </TableCell>
              </TableRow>
            ) : (
              memberCgProgramsList.map((prog, index) => {
                const rawPriority = prog.priorityLevel || "";
                const pLevel = String(rawPriority).toLowerCase().trim();
                const priorityPill = getPriorityPill(pLevel);
                const censusIndicator = getCensusIndicator(
                  prog,
                  rawPriority,
                  memberData,
                );
                const formattedNextAssessment = getFormattedNextAssessment(
                  prog,
                  getNextScheduledAssessment,
                );
                const subReason =
                  prog.subReason ||
                  prog.sub_reason ||
                  prog.outcomeReason ||
                  prog.outcome_reason ||
                  "";
                const statusText =
                  prog.status && prog.status !== "-" ? prog.status : "N/A";
                const outcomeColor = getOutcomeColor(prog, statusText);

                return (
                  <TableRow
                    key={prog.id || `prog-${index}`}
                    sx={{
                      "&:hover": { backgroundColor: "#F7F8FB" },
                      "& td": { borderBottom: "1px solid #E0E0E0" },
                    }}
                  >
                    {/* Program Name */}
                    <TableCell
                      sx={{
                        py: 1.5,
                        verticalAlign: "middle",
                        ...CELL_BORDER,
                      }}
                    >
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          border: "1px solid #ECECF1",
                          backgroundColor: "#ECECF1",
                          borderRadius: "40px",
                          px: 1.5,
                          py: 0.5,
                          height: "36px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {getCategoryIcon(prog.name)}
                        <span
                          style={{
                            color: "#1B2F3F",
                            fontWeight: 700,
                            fontSize: "14px",
                            marginLeft: "8px",
                          }}
                        >
                          {prog.name && prog.name !== "-" ? prog.name : "N/A"}
                        </span>
                      </Box>
                    </TableCell>

                    {/* Program Priority */}
                    <TableCell
                      sx={{
                        py: 1.5,
                        verticalAlign: "middle",
                        ...CELL_BORDER,
                      }}
                    >
                      {priorityPill ? (
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <span
                            style={{
                              backgroundColor: priorityPill.bgColor,
                              color: priorityPill.textColor,
                              border: `1px solid ${priorityPill.borderColor}`,
                              padding: "2px 8px",
                              borderRadius: "4px",
                              fontSize: "11px",
                              fontWeight: 700,
                              textTransform: "uppercase",
                              display: "inline-flex",
                              alignItems: "center",
                              lineHeight: "1",
                              height: "20px",
                              boxSizing: "border-box",
                              letterSpacing: "0.5px",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {priorityPill.text}
                          </span>
                          {censusIndicator && (
                            <span
                              style={{
                                backgroundColor: "#ECECF1",
                                color: "#403C6F",
                                padding: "2px 6px",
                                borderRadius: "4px",
                                fontSize: "11px",
                                fontWeight: 700,
                                textTransform: "uppercase",
                                display: "inline-flex",
                                alignItems: "center",
                                lineHeight: "1",
                                height: "20px",
                                boxSizing: "border-box",
                                border: "1px solid #ECECF1",
                              }}
                            >
                              {censusIndicator}
                            </span>
                          )}
                        </Box>
                      ) : (
                        "N/A"
                      )}
                    </TableCell>

                    {/* Program Status (outcome and timestamp) */}
                    <TableCell
                      sx={{
                        py: 1.5,
                        verticalAlign: "middle",
                        color: "#1B2F3F",
                        fontWeight: 500,
                        fontSize: "14px",
                        ...CELL_BORDER,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.5,
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 600,
                            fontSize: "16px",
                            lineHeight: "150%",
                            color: outcomeColor,
                          }}
                        >
                          {statusText === "N/A" ? "N/A" : statusText}
                        </Typography>
                        {subReason && (
                          <Typography
                            sx={{
                              fontWeight: 500,
                              fontSize: "12px",
                              color: "#7F7C9F",
                              mt: 0.25,
                            }}
                          >
                            ({capitalizeFirstLetter(subReason)})
                          </Typography>
                        )}
                        {prog.outcomeTimestamp && (
                          <Typography
                            sx={{
                              color: "#1B2F3F",
                              fontWeight: 500,
                              fontSize: "16px",
                              lineHeight: "150%",
                            }}
                          >
                            {formatDate(prog.outcomeTimestamp)}{" "}
                            {formatTimeOnly(prog.outcomeTimestamp)}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>

                    {/* Assigned RN */}
                    <TableCell
                      sx={{
                        py: 1.5,
                        verticalAlign: "middle",
                        fontSize: "14px",
                        fontWeight: 600,
                        color: "#403C6F",
                        ...CELL_BORDER,
                      }}
                    >
                      {prog.assignedRn && prog.assignedRn !== "-"
                        ? prog.assignedRn
                        : "N/A"}
                    </TableCell>

                    {/* Next Scheduled Assessment */}
                    <TableCell
                      sx={{
                        py: 1.5,
                        verticalAlign: "middle",
                        ...CELL_BORDER,
                      }}
                    >
                      {formattedNextAssessment}
                    </TableCell>

                    {/* Actions (if Biosimilar program present) */}
                    {hasBiosimilarProgram && (
                      <TableCell
                        sx={{
                          py: 1.5,
                          verticalAlign: "middle",
                          ...CELL_BORDER,
                        }}
                      >
                        {checkIsBiosimilar(prog) ? (
                          <Button
                            variant="outlined"
                            size="small"
                            disabled={canWrite === false}
                            onClick={() => onAddProgramOutcome?.(prog)}
                            sx={{
                              color: "#403C6F",
                              borderColor: "#403C6F",
                              textTransform: "none",
                              border: "1px solid #403C6F",
                              borderRadius: "20px",
                              fontWeight: 500,
                              fontSize: "13px",
                              px: 1.5,
                              py: 0.5,
                              background: "#ffffff",
                              whiteSpace: "nowrap",
                              "&:hover": {
                                backgroundColor: "rgba(64, 60, 111, 0.04)",
                                borderColor: "#403C6F",
                              },
                              "&.Mui-disabled": {
                                cursor: "not-allowed",
                                color: "#999893",
                                borderColor: "#E8E8E9",
                              },
                            }}
                          >
                            Add Program Outcome
                          </Button>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

ClinicalProgramsTable.propTypes = {
  memberCgProgramsList: PropTypes.array,
  memberData: PropTypes.object,
  getNextScheduledAssessment: PropTypes.func.isRequired,
  getCategoryIcon: PropTypes.func.isRequired,
  canWrite: PropTypes.bool,
  onAddProgramOutcome: PropTypes.func,
};

export default ClinicalProgramsTable;
